/**
 * F43 (Track B) — Admin consumer-user management list.
 *
 * Route: /users
 * Permission: users:read
 *
 * Search (email / phone / name) + filters (account type, status, has-active-
 * reports, suspension-history, verification tick, city, sport) + 4 KPI Stat
 * cards (signups today / active reports / pending blue ticks / pending GDPR) +
 * row-selection bulk-action toolbar (gated `users:bulk` → the BulkActionsDrawer)
 * + pagination. Clicking a row navigates to /users/:id.
 *
 * Data source: the F43 `adminUsersApi` (aliased to its fixture in tests). The
 * consumer-user API lives under `/admin/platform-users/*`; the SPA route is
 * `/users` (replaced the F06 placeholder).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  SearchInput,
  Select,
  Skeleton,
  Stat,
  SuspendDialog,
  Table,
  Tooltip,
  type TableColumn,
  useDebouncedValue,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type {
  AdminUserListItem,
  AdminUserVerificationFilter,
  BulkActionResponse,
  UserStatus,
} from '@bravemove/types';
import { ADMIN_USER_VERIFICATION_FILTERS, SPORT_NAME_VALUES } from '@bravemove/types';
import {
  adminUsersApi,
  type AdminUserListMeta,
  type AdminUserListQuery,
} from '../../api/adminUsers';
import { getGeo } from '../../api/geo';
import { useAuth } from '../../auth/useAuth';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';
import { BulkActionsDrawer, type BulkKind } from '../../components/moderation/BulkActionsDrawer';
import { InertTick } from '../../components/verification/EntityVerificationCard';
import { CreateUserDialog } from './CreateUserDialog';

const PAGE_LIMIT = 25;

const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> = {
  active: 'success',
  pending_verification: 'info',
  suspended: 'warning',
  disabled: 'neutral',
  deleted: 'danger',
};

/**
 * One-sentence explanation per user status, shown in a tooltip on the status
 * badge in the users table. English fallbacks; the live copy is the
 * `moderation:user.status_help.<status>` i18n key.
 */
const STATUS_HELP: Record<string, string> = {
  active: 'Active — the user can sign in and use the app normally.',
  pending_verification:
    "Signed up but hasn't verified their email yet — they can't sign in until they do.",
  suspended:
    'Temporarily blocked by an admin. Signed out everywhere; their email stays reserved (a ban).',
  disabled: 'Deactivated and unable to sign in.',
  deleted: 'Soft-deleted. The account is closed; its email is freed for re-registration.',
};

const ACCOUNT_TYPES = [
  'B2C',
  'venue_owner',
  'trainer',
  'club_admin',
  'tournament_organizer',
] as const;

/** F09 v2 (E41) — narrow the `?verification=` URL param to the frozen filter values. */
function asVerificationFilter(raw: string | null): AdminUserVerificationFilter | null {
  return raw !== null && (ADMIN_USER_VERIFICATION_FILTERS as readonly string[]).includes(raw)
    ? (raw as AdminUserVerificationFilter)
    : null;
}

/** The 5 user-status values (mirrors the F43-widened `UserStatus` union). */
const USER_STATUS_VALUES: readonly UserStatus[] = [
  'pending_verification',
  'active',
  'suspended',
  'disabled',
  'deleted',
];

function buildQuery(raw: {
  q: string | null;
  accountType: string | null;
  status: string | null;
  hasActiveReports: boolean;
  suspensionHistory: boolean;
  verification: AdminUserVerificationFilter | null;
  city: string | null;
  sport: string | null;
  page: number;
}): AdminUserListQuery {
  return {
    perPage: PAGE_LIMIT,
    page: raw.page,
    ...(raw.q ? { q: raw.q } : {}),
    ...(raw.accountType ? { accountType: raw.accountType } : {}),
    ...(raw.status ? { status: raw.status as never } : {}),
    ...(raw.hasActiveReports ? { hasActiveReports: true } : {}),
    ...(raw.suspensionHistory ? { suspensionHistory: true } : {}),
    ...(raw.verification ? { verification: raw.verification } : {}),
    ...(raw.city ? { city: raw.city } : {}),
    ...(raw.sport ? { sport: raw.sport } : {}),
  };
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminPlatformUsersPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const canBulk = auth.adminUser?.permissions?.includes('users:bulk') ?? false;
  const canBulkDelete = auth.adminUser?.permissions?.includes('users:delete') ?? false;
  const canCreate = auth.adminUser?.permissions?.includes('users:create') ?? false;
  const [createOpen, setCreateOpen] = useState(false);

  const qParam = searchParams.get('q') ?? '';
  // F61 (A4) — the (audited) list is fetched once typing pauses.
  const debouncedQ = useDebouncedValue(qParam, 300);
  const accountTypeParam = searchParams.get('accountType') ?? '';
  const statusParam = searchParams.get('status') ?? '';
  const verificationParam = asVerificationFilter(searchParams.get('verification')) ?? '';
  const cityParam = searchParams.get('city') ?? '';
  const sportParam = searchParams.get('sport') ?? '';
  const hasReportsParam = searchParams.get('hasActiveReports') === 'true';
  const suspensionParam = searchParams.get('suspensionHistory') === 'true';
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [rows, setRows] = useState<readonly AdminUserListItem[]>([]);
  const [meta, setMeta] = useState<AdminUserListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkKind, setBulkKind] = useState<BulkKind | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteResult, setDeleteResult] = useState<BulkActionResponse | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [cityOptions, setCityOptions] = useState<readonly { code: string; name: string }[]>([]);

  const listRequestSeq = useRef(0);
  const fetchList = useCallback(
    async (opts: AdminUserListQuery) => {
      // Only the newest request may fill the table: a slow older response must
      // not replace the list the admin is now looking at.
      const seq = ++listRequestSeq.current;
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const res = await adminUsersApi.list(opts);
        if (seq !== listRequestSeq.current) return;
        setRows(res.data);
        setMeta(res.meta);
      } catch (err) {
        if (seq !== listRequestSeq.current) return;
        setError(translateApiError(err, t));
      } finally {
        if (seq === listRequestSeq.current) setLoading(false);
      }
    },
    [t],
  );

  const currentQuery = (): AdminUserListQuery =>
    buildQuery({
      q: debouncedQ || null,
      accountType: accountTypeParam || null,
      status: statusParam || null,
      hasActiveReports: hasReportsParam,
      suspensionHistory: suspensionParam,
      verification: verificationParam || null,
      city: cityParam || null,
      sport: sportParam || null,
      page: pageParam,
    });

  useEffect(() => {
    // Bulk actions may only reach rows the admin can see. The search is debounced,
    // so rows ticked before this reload would otherwise stay selected once hidden.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedIds(new Set());
    void fetchList(currentQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedQ,
    accountTypeParam,
    statusParam,
    verificationParam,
    cityParam,
    sportParam,
    hasReportsParam,
    suspensionParam,
    pageParam,
  ]);

  // City filter options — Lebanon's cities (F04 /geo/cities), loaded once.
  useEffect(() => {
    let cancelled = false;
    getGeo<{ data: ReadonlyArray<{ code: string; name: string }> }>('/geo/cities')
      .then((res) => {
        if (!cancelled) setCityOptions(res.data.map((c) => ({ code: c.code, name: c.name })));
      })
      .catch(() => {
        /* non-fatal — the city filter just stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function setParam(key: string, value: string) {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  }

  function goToPage(page: number) {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  }

  /** Clear every filter + search. */
  function clearFilters() {
    setSelectedIds(new Set());
    setSearchParams(new URLSearchParams());
  }

  /**
   * F43-FC §6.7 — bulk hard-delete (super-admin only). The confirm comes from
   * the shared `SuspendDialog` (`DELETE` phrase + reason); on success the result
   * drives the shared `BulkActionsDrawer` (partial-success per-target + CSV).
   */
  const runBulkDelete = async (reason: string) => {
    setDeleteSubmitting(true);
    try {
      const res = await adminUsersApi.bulkDelete(
        { targetIds: [...selectedIds], reason },
        // A stable idempotency key per confirm (irreversible op; replay no-ops).
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `bulk-del-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      );
      setDeleteResult(res);
      setDeleteOpen(false);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const anyFilterActive = Boolean(
    qParam ||
    accountTypeParam ||
    statusParam ||
    verificationParam ||
    cityParam ||
    sportParam ||
    hasReportsParam ||
    suspensionParam,
  );

  // First option doubles as the filter's placeholder (the field name), so the
  // bare selects stay self-describing without visible FormField labels.
  const accountTypeOptions = useMemo(
    () => [
      { value: '', label: t('admin:f43.filter.accountType') },
      ...ACCOUNT_TYPES.map((a) => ({ value: a, label: t(`admin:f43.accountType.${a}`) })),
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('admin:f43.filter.status') },
      ...USER_STATUS_VALUES.map((s) => ({ value: s, label: t(`moderation:user.status.${s}`) })),
    ],
    [t],
  );

  const verificationOptions = useMemo(
    () => [
      { value: '', label: t('admin:verification.integrations.filterAll') },
      { value: 'any', label: t('admin:verification.integrations.filterAny') },
      { value: 'player', label: t('admin:verification.integrations.filterPlayer') },
    ],
    [t],
  );

  const hasReportsOptions = useMemo(
    () => [
      { value: '', label: t('admin:f43.filter.hasActiveReports') },
      { value: 'true', label: t('admin:f43.filter.yes') },
    ],
    [t],
  );

  const suspensionOptions = useMemo(
    () => [
      { value: '', label: t('admin:f43.filter.suspensionHistory') },
      { value: 'true', label: t('admin:f43.filter.yes') },
    ],
    [t],
  );

  const citySelectOptions = useMemo(
    () => [
      { value: '', label: t('admin:f43.filter.city') },
      ...cityOptions.map((c) => ({ value: c.code, label: c.name })),
    ],
    [cityOptions, t],
  );

  const sportSelectOptions = useMemo(
    () => [
      { value: '', label: t('admin:f43.filter.sport') },
      ...SPORT_NAME_VALUES.map((s) => ({
        value: s,
        label: s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      })),
    ],
    [t],
  );

  const columns = useMemo<readonly TableColumn<AdminUserListItem>[]>(
    () => [
      {
        key: 'user',
        header: t('admin:f43.column.user'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium text-[13px] text-ink truncate">{row.displayName}</span>
            <span className="font-mono text-[11px] text-ink-3 truncate">{row.email}</span>
          </div>
        ),
      },
      {
        key: 'roles',
        header: t('admin:f43.column.roles'),
        cell: (row) => (
          <div className="flex flex-wrap gap-1">
            {row.roles.map((r) => (
              <Badge key={r} tone="neutral" size="sm">
                {t(`admin:f43.role.${r}`, { defaultValue: r })}
              </Badge>
            ))}
          </div>
        ),
      },
      // F09 v2 (§13 row 21) — one inert tick per role, from the booleans only.
      {
        key: 'playerVerified',
        header: t('admin:verification.integrations.playerTick'),
        cell: (row) =>
          row.playerVerified === true ? (
            <InertTick verified entityType="player" testId={`f43-player-tick-${row.id}`} />
          ) : (
            <span className="text-[12px] text-ink-3">—</span>
          ),
      },
      {
        key: 'trainerVerified',
        header: t('admin:verification.integrations.trainerTick'),
        cell: (row) =>
          row.trainerVerified === true ? (
            <InertTick verified entityType="trainer" testId={`f43-trainer-tick-${row.id}`} />
          ) : (
            <span className="text-[12px] text-ink-3">—</span>
          ),
      },
      {
        key: 'status',
        header: t('admin:f43.column.status'),
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            <Tooltip
              content={t(`moderation:user.status_help.${row.status}`, {
                defaultValue: STATUS_HELP[row.status] ?? row.status,
              })}
            >
              <span className="inline-flex" data-testid={`f43-status-badge-${row.id}`}>
                <Badge tone={STATUS_TONES[row.status] ?? 'neutral'} size="sm">
                  {t(`moderation:user.status.${row.status}`, { defaultValue: row.status })}
                </Badge>
              </span>
            </Tooltip>
            {row.hasActiveReportsAgainst ? (
              <Tooltip
                content={t('moderation:user.status_help.reported', {
                  defaultValue: 'This user has one or more open reports filed against them.',
                })}
              >
                <span className="inline-flex" data-testid={`f43-reports-badge-${row.id}`}>
                  <Badge tone="danger" size="sm">
                    {t('admin:f43.badge.reported')}
                  </Badge>
                </span>
              </Tooltip>
            ) : null}
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: t('admin:f43.column.joined'),
        cell: (row) => (
          <span className="text-[12px] text-ink-3">{formatDate(row.createdAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  const kpis: readonly { key: string; label: string; value: number }[] = useMemo(() => {
    const m = meta;
    return [
      {
        key: 'total',
        label: t('admin:f43.kpi.total', { defaultValue: 'Total users' }),
        value: m?.total ?? 0,
      },
      {
        key: 'signups_today',
        label: t('moderation:queue.kpi.signups_today'),
        value: m?.signupsToday ?? 0,
      },
      {
        key: 'active_reports',
        label: t('moderation:queue.kpi.active_reports'),
        value: m?.activeReports ?? 0,
      },
      {
        key: 'pending_blue_ticks',
        label: t('moderation:queue.kpi.pending_blue_ticks'),
        value: m?.pendingBlueTicks ?? 0,
      },
      {
        key: 'pending_gdpr',
        label: t('moderation:queue.kpi.pending_gdpr'),
        value: m?.pendingGdpr ?? 0,
      },
    ];
  }, [meta, t]);

  return (
    <div className="space-y-4" data-testid="admin-platform-users-page">
      <PageHeader
        title={t('moderation:user.title')}
        actions={
          canCreate ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCreateOpen(true)}
              data-testid="f43fc-create-user-button"
            >
              {t('admin:f43fc.createUser.title', { defaultValue: 'Create user' })}
            </Button>
          ) : undefined
        }
      />

      {/* KPI summary */}
      <section
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
        data-testid="f43-users-kpi"
      >
        {kpis.map((k) => (
          <div key={k.key} data-testid={`f43-users-kpi-${k.key}`}>
            <Stat label={k.label} value={String(k.value)} />
          </div>
        ))}
      </section>

      {/* Controls — search, then the compact filter row. All filters are
          URL-synced, so the active query restores on refresh. The bare selects
          use their first option as a placeholder (the field name). */}
      <div className="space-y-2.5" data-testid="f43-users-toolbar">
        <SearchInput
          value={qParam}
          onChange={(e) => setParam('q', e.target.value)}
          placeholder={t('admin:f43.searchPlaceholder')}
          data-testid="f43-users-search"
        />

        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[170px]">
            <Select
              aria-label={t('admin:f43.filter.accountType')}
              value={accountTypeParam}
              onChange={(e) => setParam('accountType', e.target.value)}
              options={accountTypeOptions}
              data-testid="f43-filter-account-type"
            />
          </div>
          <div className="w-[150px]">
            <Select
              aria-label={t('admin:f43.filter.status')}
              value={statusParam}
              onChange={(e) => setParam('status', e.target.value)}
              options={statusOptions}
              data-testid="f43-filter-status"
            />
          </div>
          <div className="w-[150px]">
            <Select
              aria-label={t('admin:f43.filter.verification')}
              value={verificationParam}
              onChange={(e) => setParam('verification', e.target.value)}
              options={verificationOptions}
              data-testid="f43-filter-verification"
            />
          </div>
          <div className="w-[170px]">
            <Select
              aria-label={t('admin:f43.filter.hasActiveReports')}
              value={hasReportsParam ? 'true' : ''}
              onChange={(e) => setParam('hasActiveReports', e.target.value)}
              options={hasReportsOptions}
              data-testid="f43-filter-has-reports"
            />
          </div>
          <div className="w-[170px]">
            <Select
              aria-label={t('admin:f43.filter.suspensionHistory')}
              value={suspensionParam ? 'true' : ''}
              onChange={(e) => setParam('suspensionHistory', e.target.value)}
              options={suspensionOptions}
              data-testid="f43-filter-suspension"
            />
          </div>
          <div className="w-[170px]">
            <Select
              aria-label={t('admin:f43.filter.city')}
              value={cityParam}
              onChange={(e) => setParam('city', e.target.value)}
              options={citySelectOptions}
              data-testid="f43-filter-city"
            />
          </div>
          <div className="w-[150px]">
            <Select
              aria-label={t('admin:f43.filter.sport')}
              value={sportParam}
              onChange={(e) => setParam('sport', e.target.value)}
              options={sportSelectOptions}
              data-testid="f43-filter-sport"
            />
          </div>
          {anyFilterActive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              data-testid="f43-users-clear-filters"
            >
              {t('admin:common.clear', { defaultValue: 'Clear' })}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Bulk-action toolbar — when the actor can bulk-suspend OR bulk-delete. */}
      {(canBulk || canBulkDelete) && selectedIds.size > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-outline bg-surface-2 px-3 py-2"
          data-testid="f43-users-bulk-toolbar"
        >
          <span className="text-[13px] font-medium text-ink" data-testid="f43-users-bulk-count">
            {t('admin:f43.bulk.selected', { count: selectedIds.size })}
          </span>
          <div className="flex flex-wrap gap-2 ms-auto">
            {canBulk ? (
              <Button
                variant="secondary"
                size="xs"
                onClick={() => setBulkKind('suspend')}
                data-testid="f43-users-bulk-suspend"
              >
                {t('moderation:bulk.suspend')}
              </Button>
            ) : null}
            {canBulkDelete ? (
              <Button
                variant="destructive"
                size="xs"
                onClick={() => setDeleteOpen(true)}
                data-testid="f43-users-bulk-delete"
              >
                {t('admin:f43.bulk.delete', { defaultValue: 'Delete' })}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setSelectedIds(new Set())}
              data-testid="f43-users-bulk-clear"
            >
              {t('admin:f43.bulk.clear')}
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="team" size={36} color="currentColor" />}
          title={t('admin:f43.emptyUsers')}
        />
      ) : (
        <Table
          data={rows as AdminUserListItem[]}
          columns={columns as TableColumn<AdminUserListItem>[]}
          rowKey={(row) => row.id}
          {...(canBulk || canBulkDelete
            ? { selectable: true, selectedIds, onSelectionChange: setSelectedIds }
            : {})}
          onRowPress={(row) => navigate(`/users/${row.id}`)}
          empty={t('admin:f43.emptyUsers')}
        />
      )}

      {meta?.pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={meta.pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="f43-users-pagination"
        />
      ) : null}

      {/* Bulk-action drawer (idempotency-aware progress + partial-success CSV). */}
      {bulkKind !== null ? (
        <BulkActionsDrawer
          open
          kind={bulkKind}
          targetIds={[...selectedIds]}
          onClose={() => setBulkKind(null)}
          onDone={() => {
            setBulkKind(null);
            setSelectedIds(new Set());
            void fetchList(currentQuery());
          }}
        />
      ) : null}

      {/* Bulk hard-delete — confirm via the shared SuspendDialog (DELETE phrase +
          mandatory reason), then drive the shared BulkActionsDrawer results. */}
      <SuspendDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={runBulkDelete}
        title={t('admin:f43.bulk.delete_title', {
          defaultValue: 'Permanently delete selected users',
        })}
        confirmPhrase="DELETE"
        confirmLabel={t('admin:f43.bulk.delete', { defaultValue: 'Delete' })}
        loading={deleteSubmitting}
        reasons={[
          {
            value: 'gdpr_request',
            label: t('admin:f43.bulk.reason.gdpr', { defaultValue: 'GDPR / data-removal request' }),
          },
          {
            value: 'spam_account',
            label: t('admin:f43.bulk.reason.spam', { defaultValue: 'Spam / abusive account' }),
          },
          {
            value: 'test_account',
            label: t('admin:f43.bulk.reason.test', { defaultValue: 'Test / duplicate account' }),
          },
        ]}
        consequences={[
          t('admin:f43.bulk.delete_consequence_irreversible', {
            defaultValue:
              'This permanently removes each selected user and all cascade data. Irreversible.',
          }),
          t('admin:f43.bulk.delete_consequence_blocked', {
            defaultValue:
              'Users with bookings, businesses, reviews, or financial records are refused (suspend instead).',
          }),
        ]}
      />

      {deleteResult !== null ? (
        <BulkActionsDrawer
          open
          kind="delete"
          targetIds={[...selectedIds]}
          presetResult={deleteResult}
          onClose={() => setDeleteResult(null)}
          onDone={() => {
            setDeleteResult(null);
            setSelectedIds(new Set());
            void fetchList(currentQuery());
          }}
        />
      ) : null}

      {/* Create user — super_admin (users:create). Creates a consumer account +
          activation invite; on success the dialog shows the activation link. */}
      {canCreate ? (
        <CreateUserDialog
          open={createOpen}
          onClose={() => {
            setCreateOpen(false);
            // A user may have been created — refresh the list to surface it.
            void fetchList(currentQuery());
          }}
          onCreate={(input) => adminUsersApi.createUser(input)}
        />
      ) : null}
    </div>
  );
}
